/* eslint-disable @typescript-eslint/no-explicit-any */
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useHomeContext } from "../../contexts/HomeContext";
import useAuthHttp from "../../hooks/useAuthHttp";
import View from "../Container";
import Nav, { NavItem } from "../Nav";
import Button from "../Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { AssetMainType, IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse } from "../../models/asset";
import { useMutation, useQuery } from "@tanstack/react-query";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import FormField from "../FormField";
import { InputNumber } from "primereact/inputnumber";
import BankSearchSelector from "../BankSearchSelector";
import { Calendar } from "primereact/calendar";
import Label from "../Label";
import StockSearchSelector, { IStock } from "../StockSearchSelector";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import { useExchangeRateByType } from "../../hooks/useExchangeRateByType";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

interface IStockHandleForm {
    stock?: IStock;
    finIssue?: {
        id: string;
        name: string;
    };
    purchaseDate?: Date;
    quantity: number;
    cotation: number;
    others?: number;
    brokerage?: number;
}

interface IStockHandleFormProps {
    onSuccessRegister(assetInfo: IStockHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

const StockHandleForm = (props: IStockHandleFormProps) => {
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const http = useAuthHttp();
    const [isEdit, setIsEdit] = useState(false);

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const formik = useFormik<IStockHandleForm>({
        initialValues: {
            stock: undefined,
            finIssue: undefined,
            purchaseDate: undefined,
            quantity: 0,
            cotation: 0
        },
        validationSchema: Yup
            .object()
            .shape({
                stock: Yup.object().required("Selecione um ativo"),
                finIssue: Yup.object().required("Selecione uma instituição financeira"),
                purchaseDate: Yup.date().required("Informe a data de compra"),
                quantity: Yup.number().required("Informe a quantidade").min(1, "Informe uma quantidade maior que 0"),
                cotation: Yup.number().required("Informe a cotação").min(0.1, "Para carregar o valor de cotação uma data de compra válida deve ser informada")
            }),
        onSubmit: async (form) => await send(form)
    });

    const { exchangeRateQuery } = useExchangeRateByType(
        AssetMainType.Stock,
        formik.values.purchaseDate,
        formik.values.stock?.ticker_symbol,
        (cotation) => {
            formik.setFieldValue("cotation", cotation);
        },
        () => {
            formik.setFieldError("purchaseDate", "Cotação não encontrada para data informada");
        }
    );

    const insertAssetMutation = useMutation({
        mutationFn: async (form: IStockHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped
                    .find(listType => listType.groupName.startsWith("Ações"))
                    ?.names[0]
                    .id ?? "",
                asset_type: "ST",
                asset_fin_issue_id: form.finIssue?.id,
                asset_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_st_details: {
                    st_id: exchangeRateQuery.data?.id,
                    st_qtd_prch: form.quantity,
                    st_value_prch: form.cotation * form.quantity,
                    st_oc_brokerage: form.brokerage,
                    st_oc_others: form.others,
                    st_value_vna: form.cotation,
                    st_ticker_symbol: form.stock?.ticker_symbol
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateAssetMutation = useMutation({
            mutationFn: async (form: IStockHandleForm) => {
                const body = {
                    asset_id: assetListTypeGrouped
                        .find(listType => listType.groupName.startsWith("Ações"))
                        ?.names[0]
                        .id ?? "",
                    asset_type: "ST",
                    asset_fin_issue_id: form.finIssue?.id,
                    asset_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                    asset_st_details: {
                        st_id: exchangeRateQuery.data?.id,
                        st_qtd_prch: form.quantity,
                        st_value_prch: form.cotation * form.quantity,
                        st_oc_brokerage: form.brokerage,
                        st_oc_others: form.others,
                        st_value_vna: form.cotation,
                        st_ticker_symbol: form.stock?.ticker_symbol
                    }
                };
    
                return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
            }
        });

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                setIsEdit(false);
                return {
                    asset_info: null
                };
            }

            setIsEdit(true);
            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            return;
        }
        const assetDetails = assetDetailsQuery.data;
        formik.setValues({
            finIssue: {
                id: assetDetails.asset_info?.asset_fin_issue.id,
                name: assetDetails.asset_info.asset_fin_issue.inst_name
            },
            stock: {
                ticker_symbol: assetDetails.asset_info?.asset_st_details?.st_ticker_symbol,
                company_short_name: assetDetails.asset_info.asset_st_details?.st_ticker_symbol
            },
            purchaseDate: moment(assetDetails.asset_info.asset_purchase_date).toDate(),
            quantity: assetDetails.asset_info?.asset_st_details?.st_qtd_prch,
            cotation: assetDetails.asset_info?.asset_st_details?.st_value_prch,
            brokerage: assetDetails.asset_info?.asset_st_details?.st_oc_brokerage,
            others: assetDetails.asset_info?.asset_st_details?.st_oc_others
        });
    }, [assetDetailsQuery.data]);

    useEffect(() => {
        if (!props.initialData?.asset_st_details) {
            return;
        }
        const data = props.initialData;
        formik.setValues({
            stock: {
                ticker_symbol: data.asset_st_details?.st_ticker_symbol || "",
                company_short_name: data.asset_alias
            },
            purchaseDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            quantity: data.asset_st_details?.st_qtd_prch || 0,
            cotation: data.asset_st_details?.st_value_vna || 0,
            finIssue: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const upsert = async (assetPayload: IStockHandleForm) => {
        if (isEdit) return await updateAssetMutation.mutateAsync(assetPayload);

        return await insertAssetMutation.mutateAsync(assetPayload);
    }

    const send = async (form: IStockHandleForm) => {
        if (!form || insertAssetMutation.isPending) {
            return Promise.resolve();
        }

        try {
            const assetInfo = await upsert(form);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Ações</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <StockSearchSelector
                        selected={formik.values.stock}
                        select={(stock) => {
                            formik.setFieldValue("stock", stock);
                        }}
                        error={formik.errors.stock}
                        hasError={!!formik.errors.stock && formik.touched.stock}
                    />

                    <BankSearchSelector
                        hasError={!!formik.errors.finIssue && formik.touched.finIssue}
                        error={formik.errors.finIssue}
                        selected={formik.values.finIssue}
                        label="Onde você comprou?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("finIssue", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <FormField
                        id="purchaseDate"
                        label="Quando você comprou?"
                        hasError={!!formik.errors.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                            showIcon
                            disabledDays={[0, 6]}
                            icon={exchangeRateQuery.isLoading ? "pi pi-spin pi-spinner" : "pi pi-calendar"}
                            readOnlyInput
                            onChange={(event) => {
                                formik.setFieldValue("purchaseDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <FormField
                        id="quantity"
                        hasError={!!formik.errors.quantity && formik.touched.quantity}
                        error={formik.errors.quantity}
                        label="Quantidade"
                    >
                        <InputNumber
                            invalid={!!formik.errors.quantity && formik.touched.quantity}
                            value={formik.values.quantity}
                            minFractionDigits={2}
                            maxFractionDigits={10}
                            onValueChange={(e) =>
                                formik.setFieldValue("quantity", e.value)
                            }
                            locale="pt-BR"
                        />
                    </FormField>

                    <FormField
                        id="cotation"
                        hasError={!!formik.errors.cotation && formik.touched.cotation}
                        error={formik.errors.cotation}
                        label="Cotação"
                    >
                        <InputNumber
                            invalid={!!formik.errors.cotation && formik.touched.cotation}
                            value={formik.values.cotation}
                            onValueChange={(e) =>
                                formik.setFieldValue("cotation", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                            minFractionDigits={formik.values.cotation.toString().split(".")[1]?.length ?? 4}
                        />
                    </FormField>

                    <View flexColumn gap={2}>
                        <Label>Corretagem</Label>
                        <InputNumber
                            value={formik.values.brokerage}
                            onValueChange={(e) =>
                                formik.setFieldValue("brokerage", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View flexColumn gap={2}>
                        <Label>Outros custos</Label>
                        <InputNumber
                            value={formik.values.others}
                            onValueChange={(e) =>
                                formik.setFieldValue("others", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <FormField
                        id="totalValue"
                        label="Valor total de compra"
                    >
                        <InputNumber
                            disabled
                            value={((formik.values?.cotation ?? 0)) * (formik.values.quantity ?? 0)}
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertAssetMutation.isPending}
                            disabled={insertAssetMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default StockHandleForm;